"use client";

import { useCallback, useRef, useState } from "react";
import Image from "next/image";
import { Link } from "@/navigation";
import { useTranslations } from "next-intl";
import { capturePostHogEvent } from "@/lib/posthog";
import { readHomeVariant } from "@/lib/home-variant";
import { useComingSoonDialog } from "@/contexts/ComingSoonDialogContext";
import { useImpression } from "@/hooks/use-impression";
import { formatPrice, type SignatureCake } from "@/lib/signatureCakes";

/**
 * Image cell for a cake card.
 *
 * For priority images (above-the-fold), the image is rendered fully opaque
 * immediately so it can serve as the LCP element. For lazy images, a
 * short opacity fade on load preserves the existing skeleton UX without
 * delaying the priority image's paint.
 */
function CakeImageCell({
  src,
  alt,
  sizes,
  priority,
  isLegacyWebsiteEnabled,
}: {
  src: string;
  alt: string;
  sizes: string;
  priority: boolean;
  isLegacyWebsiteEnabled: boolean;
}) {
  // Priority images must be opaque on first paint (LCP candidate).
  // Non-priority images keep the existing skeleton shimmer + fade-in.
  const [isLoaded, setIsLoaded] = useState(priority);
  return (
    <div
      className={`cake-img relative aspect-square overflow-hidden rounded-2xl flex-shrink-0 mb-2 sm:mb-3 shadow-md transition-all duration-200 ${
        isLegacyWebsiteEnabled ? "" : "group-hover:shadow-xl"
      } ${!isLoaded ? "skeleton-shimmer" : "bg-muted"}`}
    >
      <Image
        src={src}
        alt={alt}
        fill
        sizes={sizes}
        className={`object-cover transition-opacity duration-500 ${
          isLegacyWebsiteEnabled ? "" : "md:group-hover:scale-110 transition-transform duration-200"
        } ${isLoaded ? "opacity-100" : "opacity-0"}`}
        onLoad={() => setIsLoaded(true)}
        quality={75}
        priority={priority}
        fetchPriority={priority ? "high" : undefined}
        loading={priority ? undefined : "lazy"}
      />
    </div>
  );
}

/**
 * One `sizes` string now serves two layouts: fixed-width rail cards (variant A)
 * and fluid grid tracks (variant B). Each entry is >= the widest the card gets
 * in EITHER arm — over-requesting costs a few KB, while under-requesting
 * upscales the image and visibly softens it.
 *
 * Widest per band, A (fixed) vs B (grid track):
 *   <640px    A 180  ·  B 2 cols ≈ 171 at 390px   → 180
 *   640–767   A 280  ·  B 2 cols ≈ 314 at 700px   → 320  (was 280: too small for B)
 *   768–1023  A 320  ·  B 3 cols ≈ 260 at 900px   → 320
 *   >=1024    A 350  ·  B 4 cols ≈ 350 at 1536px  → 350
 */
const CARD_IMAGE_SIZES = "(min-width: 1024px) 350px, (min-width: 640px) 320px, 180px";

export interface CakeCardProps {
  cake: SignatureCake;
  /** Raw (untranslated) category key this cake was grouped under. */
  category: string;
  /** 0-based rank of this cake inside its category rail/section. */
  positionInCategory: number;
  /** 0-based rank across every cake rendered by this grid. */
  positionOverall: number;
  /**
   * Where this card lives, e.g. `"homepage"` or `"category_page"`. Kept as a
   * property rather than baked into the event name so all surfaces roll up in
   * one funnel and can still be split apart.
   */
  surface: string;
  prioritizeImage?: boolean;
  isValentineEnabled?: boolean;
  isLegacyWebsiteEnabled?: boolean;
  linkPrefetch?: boolean;
  /** Emit `cake_card_impression`. Off outside the instrumented surfaces. */
  trackImpressions?: boolean;
}

/**
 * A single cake card, plus its analytics.
 *
 * The card previously reported only `cake_id` + `cake_name` on click. That made
 * the one question worth asking unanswerable: whether the long tail of cakes is
 * unpopular or merely unseen. `position_in_category` and the paired
 * `cake_card_impression` are what let a click rate be computed per slot.
 */
export function CakeCard({
  cake,
  category,
  positionInCategory,
  positionOverall,
  surface,
  prioritizeImage = false,
  isValentineEnabled = false,
  isLegacyWebsiteEnabled = false,
  linkPrefetch = true,
  trackImpressions = false,
}: CakeCardProps) {
  const t = useTranslations("cakesGrid");
  const { openComingSoon } = useComingSoonDialog();
  const cardRef = useRef<HTMLElement | null>(null);

  // Built lazily at fire time, not at render: `readHomeVariant` reads a DOM
  // attribute set by a pre-paint script, and calling it during render would
  // make the component's output depend on something React did not render —
  // a hydration mismatch waiting to happen.
  const buildEventProperties = useCallback(
    () => ({
      cake_id: cake.id,
      cake_name: cake.name,
      cake_slug: cake.slug ?? cake.id,
      category,
      position_in_category: positionInCategory,
      position_overall: positionOverall,
      price_cents: cake.basePrice,
      surface,
      home_variant: readHomeVariant(),
    }),
    [cake.id, cake.name, cake.slug, cake.basePrice, category, positionInCategory, positionOverall, surface],
  );

  useImpression(
    cardRef,
    useCallback(() => {
      capturePostHogEvent("cake_card_impression", buildEventProperties());
    }, [buildEventProperties]),
    trackImpressions,
  );

  const cardClassName =
    "cake-card group relative flex flex-col flex-shrink-0 w-[180px] sm:w-[280px] md:w-[320px] lg:w-[350px] transition-all duration-200 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

  // Category-derived badge, variant B only. The artifact's grid has no category
  // headings, so the badge is what carries that information instead.
  const badgeLabel =
    category === "bestsellers" ? t("badgeBestseller") : null;

  const cardContent = (
    <>
      {badgeLabel && <span className="cake-badge">{badgeLabel}</span>}
      <CakeImageCell
        src={cake.image}
        alt={t("imageAlt", { name: cake.name })}
        sizes={CARD_IMAGE_SIZES}
        priority={prioritizeImage}
        isLegacyWebsiteEnabled={isLegacyWebsiteEnabled}
      />

      <div className="cake-body flex flex-col min-h-[90px] sm:min-h-[100px]">
        <p
          className={`text-xs sm:text-sm font-medium text-foreground mb-1 line-clamp-2 ${
            isLegacyWebsiteEnabled
              ? ""
              : "group-hover:text-primary transition-colors duration-200"
          }`}
        >
          {cake.name}
        </p>

        {/* Product context */}
        <div className="cake-meta flex flex-col gap-1 mb-2">
          {cake.customization?.allowedServings &&
            cake.customization.allowedServings.length > 0 && (
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {t("serves", {
                  min: cake.customization.allowedServings[0],
                  max: cake.customization.allowedServings[
                    cake.customization.allowedServings.length - 1
                  ],
                })}
              </p>
            )}
          {cake.customization?.allowedCakeFlavors &&
            cake.customization.allowedCakeFlavors.length > 0 && (
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {t("flavors", {
                  flavors: cake.customization.allowedCakeFlavors
                    .map((f) => f.charAt(0).toUpperCase() + f.slice(1))
                    .join(", "),
                })}
              </p>
            )}
          {cake.customization?.allowText && (
            <p className="cake-meta-custom text-[10px] sm:text-xs text-muted-foreground">
              {isValentineEnabled ? t("customizableTextOnly") : t("customizable")}
            </p>
          )}
        </div>

        {!isLegacyWebsiteEnabled && (
          // Wrapper is a plain block in variant A, so the control keeps today's
          // bottom-left price exactly. Variant B turns it into a baseline row
          // with the "view" affordance on the right (see globals.css).
          <div className="cake-priceline mt-auto">
            <p className="cake-price text-xs sm:text-sm font-semibold text-foreground inline-block group-hover:scale-105 transition-transform duration-200">
              {formatPrice(cake.basePrice)}
            </p>
            {/* Variant B only; hidden in the control. */}
            <span className="cake-go" aria-hidden="true">
              {t("viewCake")}
            </span>
          </div>
        )}
      </div>
    </>
  );

  if (isLegacyWebsiteEnabled) {
    return (
      <button
        ref={cardRef as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={() => {
          capturePostHogEvent("legacy_cake_clicked", buildEventProperties());
          openComingSoon({ cakeId: cake.id, cakeName: cake.name });
        }}
        className={`${cardClassName} text-left cursor-pointer`}
      >
        {cardContent}
      </button>
    );
  }

  return (
    <Link
      ref={cardRef as React.Ref<HTMLAnchorElement>}
      href={`/cakes/${cake.slug ?? cake.id}` as any}
      prefetch={linkPrefetch}
      onClick={() => capturePostHogEvent("cake_card_clicked", buildEventProperties())}
      className={cardClassName}
      // Lets E2E correlate a specific card with the impression/click event it
      // produced, instead of inferring identity from render order.
      data-cake-id={cake.id}
      data-position-in-category={positionInCategory}
    >
      {cardContent}
    </Link>
  );
}
